import React, { createContext, useContext, useEffect, useState } from 'react';
import { Experiment, Variants } from '@amplitude/experiment-js-client';
import config from '@/types/config';
import { useAppSelector } from '@/store/hooks';
import { UserData } from '@/types/user';
import { FeatureFlags } from '@/constants/featureFlags';

const prepareUserProperties = (user: UserData | null) => {
  const userProperties: Record<string, string> = {};

  if (user) {
    userProperties.userCreatedAt = new Date(user?.createdAt).getTime().toString();
  }

  return userProperties;
};

type ExperimentContextType = {
  variants: Variants | null;
  isReady: boolean;
};

const ExperimentContext = createContext<ExperimentContextType | null>(null);

type ExperimentProviderProps = {
  children: React.ReactNode;
};

const ExperimentProvider: React.FC<ExperimentProviderProps> = ({ children }) => {
  const [variants, setVariants] = useState<Variants | null>(null);
  const [isReady, setIsReady] = useState(false);
  const user = useAppSelector((state) => state.user.user);

  const initialize = async () => {
    if (!user) return;

    setIsReady(false);

    const experiment = await Experiment.initializeWithAmplitudeAnalytics(config.amplitudeApiKey, {
      debug: config.nodeEnv === 'development',
    });

    await experiment.fetch({
      user_id: `user_id_${user?.id?.toString()}`,
      user_properties: prepareUserProperties(user),
    });

    const keys = await experiment.all();

    setVariants(keys);
    setIsReady(true);
  };

  useEffect(() => {
    initialize();
  }, [user?.id]);

  return (
    <ExperimentContext.Provider value={{ variants, isReady }}>
      {children}
    </ExperimentContext.Provider>
  );
};

export const useExperiment = (featureFlag: FeatureFlags) => {
  const context = useContext(ExperimentContext);

  if (!context) {
    throw new Error('useExperiment must be used within an ExperimentProvider');
  }

  const { key: variant, payload } = context.variants?.[featureFlag] || {};

  return {
    variant,
    payload,
    isReady: context.isReady,
  };
};

export default ExperimentProvider;
